import { Component, HostListener, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ProfileService } from './services/profile.service';
import { Profile, Location, OwnershipType, ProfileAttachment, Supplier } from './models/profile.model';
import { TabTemplateService } from './services/tab-template.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { StateService } from './services/state.service';
import { I18nService } from '../shared/services/i18n.service';
import { Contact, UserDetails } from './models/user.model';
import { TslDialog, TslToastrService } from '@tesla/ng-tds';
import { StatusEnum, Status } from './models/state.model';
import { CommonService } from './services/common.service';
import * as FileSaver from 'file-saver';
import { Store, select } from '@ngrx/store';
import { SupplierState } from './store/state/supplierstate.state';
import {
  GetSupplierProfileCurrentUser,
  ClearSupplierDetailsByCode,
  GetGeneralSettings,
  GetSupplierOtpRequestByUser,
  GetSupplierPendingTasksByCode,
  GetPreventativeInProgress,
  GetSupplierContactListByCode
} from './store/app.actions';
import { SelectCurrentUser, SelectSupplierOtpRequest,SelectPreventativeInProgress, SelectSupplierContactList } from './store/app.selector';
import { ComponentCanDeactivate } from '../core/interfaces/component-can-deactivate';
import { OnboardingContactType } from './components/onboarding/contacts-wrapper/contact-group.model';
import { SupplierTaskService } from './services/supplier-task.service';
import { TaskStatus } from './models/onboarding.model';
import { SupplierCommunicationValidation } from './models/generic.model';
import { LanguageService } from 'app/core/services/language.service';
import { OTPService } from 'app/shared/services/otp/otp.service';
import { MatDialog } from '@angular/material/dialog';
import { NameChangeFormComponent } from './components/request-form/name-change-form/name-change-form.component';
import { SecurityCheckComponent } from './components/bank/bank-grid/security-check/security-check.component';
import { MfaBannerComponent } from './components/mfa-banner/mfa-banner.component';
import { AuthData } from '@tesla/ng-auth';
import { SupplierContactPropertyValue } from './models/supplier.model';
import { BaseComponent } from './components/base.component';
import { isNullOrUndefined } from 'app/shared/utils/utils';


@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent extends BaseComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
    @ViewChild('viewContainer', { read: ViewContainerRef, static: true }) viewContainerRef: ViewContainerRef;
    panelOpenState = false;
    currentTab: string;
    tab: string = 'Address';
    tabList: string[] = [];
    supplierCode: string;
    deleteTitle: string;
    deleteConfirmation: string;

    profile: Profile = new Profile();
    obLocations$: Observable<Location[]>;
    obOwnerships$: Observable<OwnershipType[]>;
    obSupplierList$: Observable<Supplier[]>;
    userDetails: UserDetails = null;
    showSubtierOnboarding: boolean = false;
    showEntityExtensionOnboarding: boolean = false;
    showNameTinChange: boolean = false;
    otpRequest: SupplierCommunicationValidation = null;
    taskDueDate: Date = null;
    languageSub: Subscription;
    checkIfNameOrTaxChangeIsAllowedSub: Subscription;
    public language: string;
    actionList: string[];
    isPrimary: boolean;
    preventativeHold: boolean = false;
    activeMFADialog: boolean = false;
    supplierContacts: SupplierContactPropertyValue[];
    isContactValidationPendingTaskErrorMessage: string = 'No edit functionality available as your organization\'s contacts have yet to be validated, you are not enrolled in Multi-Factor Authentication, critical data changes are pending review, or you have no user access to manage this data.';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private tabTemplateService: TabTemplateService,
        private dialog: TslDialog,
        private translate: I18nService,
        private stateService: StateService,
        private commonService: CommonService,
        private service: ProfileService,
        private languageService: LanguageService,
        private store: Store<SupplierState>,
        private taskService: SupplierTaskService,
        private tslDialog: TslDialog,
        public otpService: OTPService,
        private matDialog: MatDialog,
        private toaster: TslToastrService,
        private authData: AuthData,
    ) {
        super();
        // loading user details
        this.store.dispatch(new GetGeneralSettings());
        this.store.dispatch(new GetSupplierProfileCurrentUser());
    }

    ngOnDestroy(): void {
        if (this.languageSub) this.languageSub.unsubscribe();
        if (this.checkIfNameOrTaxChangeIsAllowedSub) this.checkIfNameOrTaxChangeIsAllowedSub.unsubscribe();
    }

    ngOnInit() {
        // get language
        this.languageSub = this.languageService
            .getSelectedLanguageAsync()
            .subscribe((res) => (this.language = res || 'en-US'));

        combineLatest([this.store.pipe(select(SelectCurrentUser)), this.route.params]).subscribe(([result, params]) => {
            if (result && params) {
                if (params['code']) {
                    this.supplierCode = this.commonService.getSupplierCode(params['code']);
                }
                this.userDetails = result;
                if (this.userDetails.isSupplier && (this.supplierCode === undefined || this.supplierCode === '')) {
                    this.supplierCode = this.userDetails.supplierContact.supplierMaps[0].supplierCode;
                }

                let email = this.userDetails?.supplierContact?.emailId;
                this.isPrimary = this.userDetails?.supplierContact?.supplierMaps?.find(
                    (x) => x.contactEmailId == email,
                )?.isPrimaryContact;

                if (this.supplierCode) {
                    this.store.dispatch(new GetPreventativeInProgress(this.supplierCode, true));
                    this.store.dispatch(new GetSupplierContactListByCode(this.supplierCode));
                    this.store.pipe(select(SelectPreventativeInProgress)).subscribe((x) => {
                        if (x) {
                            this.preventativeHold = x;
                        }
                    });
                }
                if (
                    this.supplierCode &&
                    this.userDetails.isSupplier &&
                    this.userDetails.supplierContact &&
                    this.userDetails.supplierContact.supplierMaps[0]?.userRoles?.find(
                        (r) => r.role == OnboardingContactType.SUBTIERONBOARDING,
                    )
                ) {
                    this.checkIfSubtierOnboardingPending(this.userDetails.supplierContact);
                } else if (this.supplierCode && this.userDetails) {
                    this.store.dispatch(new ClearSupplierDetailsByCode());
                    this.store.dispatch(new GetSupplierOtpRequestByUser(this.supplierCode));
                    this.store.dispatch(new GetSupplierPendingTasksByCode(this.supplierCode, true));
                    this.loadResources();

                    this.showEntityExtensionOnboarding = this.commonService.isEntityExtensionState(
                        this.userDetails,
                        this.supplierCode,
                    );
                    this.showNameTinChange =
                        this.isPrimary && this.commonService.isNameTinChangeState(this.userDetails, this.supplierCode);
                    if (this.showEntityExtensionOnboarding || this.showNameTinChange) {
                        this.getSupplierTasks();
                    }
                    let redirectTo = this.commonService.getRouteFromSupplierState(this.userDetails, this.supplierCode);
                    this.router.navigate(redirectTo);
                }

                this.actionList = this.userDetails?.supplierContact?.supplierMaps.filter(
                    (x) => x.supplierCode == this.supplierCode,
                )[0].allowedWorkflows;
            }

            if(params && !isNullOrUndefined(params['code']))
            {
                this.supplierCode = params['code'];
                this.PrimaryContactDocument(this.store, this.router, this.supplierCode, false);
            }
        });

        this.store.pipe(select(SelectSupplierContactList)).subscribe((contacts) => {
            if (contacts) {
              this.supplierContacts = contacts;
              this.showMFABanner();
            }
        });
    }

    @HostListener('window:beforeunload')
    isDirty(): boolean {
        return !this.tabTemplateService.isDirty();
    }

    supplierAutocompleteDisplay(supplier: Supplier): string {
        return `${supplier.supplierCode} - ${supplier.supplierName}`;
    }

    showMFABanner() {
      let decodedAccessToken = JSON.parse(this.authData.urlBase64Decode(this.authData.getAccessToken().accessToken));
        if (this.activeMFADialog || decodedAccessToken?.amr?.includes("mfa") || this.userDetails.isOptedForMFA == false) {
            return;
        }
        const dialogRef = this.tslDialog.open(MfaBannerComponent, {
            width: '50%',
            data: { supplierCode: this.supplierCode, supplierContacts: this.supplierContacts },
        });

        this.activeMFADialog = true;

        dialogRef.afterClosed().subscribe((res) => {
            // log dialog closed response.
            this.activeMFADialog = false;
            if(res){
              window.location.reload();
            }
        });
    }

    onUploadFile(file: File): void {
        if (file.size > 10240000) {
            this.dialog.alert(this.translate.get('Alert'), this.translate.get('File size can not be more than 10MB!'));
            return;
        }

        this.service.postProfileAttachment(this.supplierCode, file).subscribe((attach) => {
            this.stateService.currentStatus.next(new Status(StatusEnum.Success, ''));
            this.profile.attachments.push(attach);
        });
    }

    onDeleteAttachment(attach: ProfileAttachment): void {
        this.dialog
            .confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.service.deleteProfileAttachment(this.supplierCode, attach.id).subscribe((_) => {
                        const ind = this.profile.attachments.indexOf(attach);
                        this.profile.attachments.splice(ind, 1);
                        this.stateService.currentStatus.next(new Status(StatusEnum.Success, ''));
                    });
                }
            });
    }

    onDownloadAttachment(attach: ProfileAttachment): void {
        this.service.downloadProfileAttachment(this.supplierCode, attach.storageKey).subscribe((file) => {
            FileSaver.saveAs(file, attach.fileName);
        });
    }

    supplierCodeChangeEventHandler(supplierCode: string) {
        this.supplierCode = supplierCode;
    }

    getSupplierWebsite(website: string) {
        if (website && !website.toLowerCase().startsWith('http')) {
            return `https://${website}`;
        }

        return website;
    }

    getFileType(file: ProfileAttachment): string {
        const nameArray = file.fileName.split('.');
        return nameArray[nameArray.length - 1];
    }

    loadResources(): void {
        this.translate.getAsync('Delete Item').subscribe((t) => (this.deleteTitle = t));

        this.translate
            .getAsync('Are you sure you want to delete this item?')
            .subscribe((t) => (this.deleteConfirmation = t));

        this.store.pipe(select(SelectSupplierOtpRequest)).subscribe((otpRequest) => {
            if (otpRequest) {
                this.otpRequest = otpRequest;
            }
        });
    }

    checkIfSubtierOnboardingPending(supplierContact: Contact) {
        this.taskService.getSupplierTasks(this.supplierCode).subscribe((tasks) => {
            if (tasks && tasks.length > 0) {
                let hasPendingTasks = tasks?.some((x) => x.taskStatus != TaskStatus.Completed);
                if (hasPendingTasks) {
                    // show link to subtier homepage
                    this.showSubtierOnboarding = true;
                    // check roles to determine whether we can directly route to tasks
                    let hasOtherRoles = supplierContact.supplierMaps[0]?.userRoles?.some(
                        (x) => !x.role.toLocaleLowerCase().includes('subtier'),
                    );
                    if (!hasOtherRoles) {
                        this.navigateToTaskHomePage();
                    } else {
                        this.router.navigate([`${this.supplierCode}`]);
                    }
                }
                // no pending tasks then load post onboarding
                else {
                    this.router.navigate([`${this.supplierCode}`]);
                }
            }
        });
    }

    getSupplierTasks() {
        this.taskService.getSupplierTasks(this.supplierCode).subscribe((tasks) => {
            if (tasks && tasks.length > 0) {
                this.taskDueDate = tasks[0].dueDate;
            }
        });
    }

    navigateToTaskHomePage() {
        this.router.navigate([`${this.supplierCode}`, 'Onboarding', 'Home']);
    }

    showOTP() {
        this.tslDialog.alert(
            'Verification Code/OTP',
            `${this.otpRequest?.otp} - If you are not expecting this verification code, no action is required.`,
        );
    }

    onActionClick(action) {
        if (this.preventativeHold == false) {
            switch (action) {
                case 'Update Legal Name/ Tax ID Number': {
                    this.checkIfNameOrTaxChangeIsAllowedSub = this.service
                        .checkIfNameOrTaxChangeIsAllowed(this.supplierCode)
                        .subscribe(
                            (res) => {
                                if (!res) {
                                    this.toaster.error(this.translate.get('Something went wrong!'));
                                    return;
                                }
                                if (!res.allowChangeRequest) {
                                    if (res.helpContext?.length) {
                                        res.helpContext.forEach((error) => {
                                            this.toaster.error(this.translate.get(error.value));
                                        });
                                    } else this.toaster.error(this.translate.get('Something went wrong!'));
                                    return;
                                }
                                if (!this.otpService.isAuthenticated()) {
                                    this.router.navigate(this.route.snapshot?.url?.map((x) => x.path));
                                    this.otpService.requestAuthentication(this.router.url);
                                    return false;
                                }

                                const dialogRef = this.matDialog.open(NameChangeFormComponent, {
                                    panelClass: ['sidepanel', 'animate__animated', 'animate__slideInRight'],
                                    width: '65%',
                                    height: '100%',
                                    minWidth: '770px',
                                    data: { supplierCode: this.supplierCode, mode: 'edit' },
                                });

                                dialogRef.afterClosed().subscribe((x) => {
                                    this.onReload(x, true);
                                });
                            },
                            (error) => {},
                        );
                    break;
                }
            }
        }
    }

    onReload(reload: boolean, forceReload: boolean = false) {
        if (reload) {
            this.store.dispatch(new GetSupplierProfileCurrentUser(null, forceReload));
            const currentUrl = this.router.url;
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigate([currentUrl]);
            });
        }
    }
}
